import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';
import { theme } from '../../theme/theme.js';
import OpportunityProductApi from '../../api/OpportunityProductApi';
import OpportunityLineItemApi from '../../api/OpportunityLineItemApi';
import QuoteLineItemApi from '../../api/QuoteLineItemApi';
import QuoteProductApi from '../../api/QuoteProductApi';
import OrderProductApi from '../../api/OrderProductApi';
import OrderLineItemApi from '../../api/OrderLineItemApi';
import {
  parseLineItemWizardConfig,
  clampLineQuantity,
  lineItemProductQuantityBounds,
  resolveProductQuantityBounds,
  formatQuantityBoundsHint,
  pickVisibleOrderedSearchKeys,
  pickVisibleOrderedEditKeys,
  getLineItemWizardSearchDisplayedText,
  getLineItemWizardEditDisplayedText,
  spreadProductSearchRowOntoLineItem,
} from './lineItemWizardConfig';

/** Per parent record copy + API-specific strings (Quote / Opportunity / Order). */
const LINE_ITEM_PARENT_CONTEXT_META = {
  opportunity: {
    parentIdLabel: 'Opportunity ID',
    defaultModalTitle: 'Add Products to Opportunity',
    currencyMissing: 'Opportunity currency is missing. Please update the record and try again.',
    cannotSearchWithoutCurrency: 'Cannot search without a valid opportunity currency.',
    loadingFallback: 'Loading opportunity products…',
    saveParentRequired: 'Opportunity ID is required to save products.',
    saveLineFallbackName: 'Opportunity Product',
  },
  quote: {
    parentIdLabel: 'Quote ID',
    defaultModalTitle: 'Add Products to Quote',
    currencyMissing: 'Quote currency is missing. Please update the record and try again.',
    cannotSearchWithoutCurrency: 'Cannot search without a valid quote currency.',
    loadingFallback: 'Loading quote products…',
    saveParentRequired: 'Quote ID is required to save products.',
    saveLineFallbackName: 'Quote Product',
  },
  order: {
    parentIdLabel: 'Order ID',
    defaultModalTitle: 'Add Products to Order',
    currencyMissing: 'Order currency is missing. Please update the record and try again.',
    cannotSearchWithoutCurrency: 'Cannot search without a valid order currency.',
    loadingFallback: 'Loading order products…',
    saveParentRequired: 'Order ID is required to save products.',
    saveLineFallbackName: 'Order Line Item',
  },
};

const WIZARD_STEPS = {
  SELECT: 1,
  EDIT: 2,
};

/** Stable default for `initialItems` — a fresh `[]` in param defaults changes every render and breaks useCallback/useEffect. */
const EMPTY_INITIAL_ITEMS = [];

const generateKey = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const mapInitialItems = (items = [], fallbackCurrency = 'USD') =>
  items.map(item => {
    const rawDiscount =
      item.discount !== undefined ? item.discount : item.discountPercent;
    const normalizedDiscount =
      rawDiscount === null || rawDiscount === undefined || rawDiscount === ''
        ? ''
        : Number(rawDiscount);

    return {
      ...lineItemProductQuantityBounds(item),
      uid: item.id || generateKey(),
      id: item.id,
      productId: item.productid || item.productId,
      productPriceId: item.productpriceid || item.productPriceId,
      productName: item.product_name || item.productName || item.name,
      productCode: item.product_code || item.productCode || '—',
      productCategory: item.product_category || item.productCategory || '—',
      currency: item.currency || fallbackCurrency,
      quantity: Number(item.quantity || 1),
      listPrice: Number(
        item.list_price !== undefined ? item.list_price : item.listPrice || 0
      ),
      salesPrice: Number(
        item.sales_price !== undefined ? item.sales_price : item.salesPrice || 0
      ),
      discountPercent:
        normalizedDiscount === '' || Number.isNaN(normalizedDiscount)
          ? ''
          : normalizedDiscount,
    };
  });

/**
 * Unified add-products wizard for Opportunity, Quote, and Order line items.
 *
 * Props:
 * - `lineItemContext` — `'opportunity' | 'quote' | 'order'`
 * - `parentId`, `parentCurrency` — record id / currency from the owning object
 * - `initialItems` — only used when `lineItemContext === 'opportunity'` and `parentId` is missing
 *
 * Thin wrappers (`OpportunityProductWizard`, …) delegate here; legacy backups: `*.legacy.js`.
 */
const LineItemProductWizard = ({
  lineItemContext = 'opportunity',
  parentId,
  parentCurrency,
  show,
  onHide,
  onSaveSuccess,
  initialItems = EMPTY_INITIAL_ITEMS,
  /** Resolved from object_config.config via parseLineItemWizardConfig (DetailLayout) */
  lineItemWizardConfig: lineItemWizardConfigProp,
}) => {
  const ctx =
    LINE_ITEM_PARENT_CONTEXT_META[lineItemContext] || LINE_ITEM_PARENT_CONTEXT_META.opportunity;
  const w = useMemo(
    () => lineItemWizardConfigProp || parseLineItemWizardConfig(null),
    [lineItemWizardConfigProp]
  );
  const str = (k, fallback) => (w.strings[k] ? w.strings[k] : fallback);
  const visSearchKeys = useMemo(() => pickVisibleOrderedSearchKeys(w), [w]);
  const visEditKeys = useMemo(() => pickVisibleOrderedEditKeys(w), [w]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [apiError, setApiError] = useState(null);
  const [currentStep, setCurrentStep] = useState(WIZARD_STEPS.SELECT);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [deletedIds, setDeletedIds] = useState([]);
  const [initialItemsLoading, setInitialItemsLoading] = useState(false);

  const selectedProductsList = useMemo(() => selectedProducts, [selectedProducts]);
  const newProductsCount = useMemo(
    () => selectedProductsList.filter(item => !item.id).length,
    [selectedProductsList]
  );
  const selectedCountByPriceId = useMemo(() => {
    const map = {};
    selectedProductsList.forEach(item => {
      const key = item.productPriceId || item.productId;
      if (!key) {
        return;
      }
      map[key] = (map[key] || 0) + 1;
    });
    return map;
  }, [selectedProductsList]);

  const hydrateSelectedProducts = useCallback(
    items => {
      if (Array.isArray(items) && items.length > 0) {
        const mapped = mapInitialItems(items, parentCurrency).map(item => ({
          ...item,
          quantity: clampLineQuantity(item.quantity, item),
        }));
        setSelectedProducts(mapped);
        setCurrentStep(WIZARD_STEPS.EDIT);
      } else {
        setSelectedProducts([]);
        setCurrentStep(WIZARD_STEPS.SELECT);
      }
      setDeletedIds([]);
      setSaveError(null);
    },
    [parentCurrency]
  );

  const fetchExistingLineItems = useCallback(async () => {
    if (lineItemContext === 'opportunity') {
      if (!parentId) {
        hydrateSelectedProducts(initialItems);
        return;
      }
      setInitialItemsLoading(true);
      try {
        const response = await OpportunityLineItemApi.fetchByOpportunity(parentId);
        if (response?.success) {
          hydrateSelectedProducts(response.data || []);
        } else {
          hydrateSelectedProducts([]);
        }
      } catch (error) {
        console.error('Failed to load opportunity line items:', error);
        hydrateSelectedProducts([]);
      } finally {
        setInitialItemsLoading(false);
      }
      return;
    }

    if (!parentId) {
      hydrateSelectedProducts([]);
      return;
    }
    setInitialItemsLoading(true);
    try {
      let response;
      if (lineItemContext === 'quote') {
        response = await QuoteLineItemApi.fetchByQuote(parentId);
      } else {
        response = await OrderLineItemApi.fetchByOrder(parentId);
      }
      if (response?.success) {
        hydrateSelectedProducts(response.data || []);
      } else {
        hydrateSelectedProducts([]);
      }
    } catch (error) {
      console.error('Failed to load line items:', error);
      hydrateSelectedProducts([]);
    } finally {
      setInitialItemsLoading(false);
    }
  }, [lineItemContext, parentId, hydrateSelectedProducts, initialItems]);

  useEffect(() => {
    if (show) {
      fetchExistingLineItems();
    } else {
      setSelectedProducts([]);
      setCurrentStep(WIZARD_STEPS.SELECT);
      setSearchTerm('');
      setSearchResults([]);
      setApiError(null);
      setDeletedIds([]);
      setSaveError(null);
    }
  }, [show, fetchExistingLineItems]);

  const performSearch = async (term = searchTerm) => {
    if (!parentCurrency) {
      setApiError(ctx.currencyMissing);
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setApiError(null);

    try {
      const response =
        lineItemContext === 'order'
          ? await OrderProductApi.searchProducts({
              currency: parentCurrency,
              search: term,
            })
          : await OpportunityProductApi.searchProducts({
              currency: parentCurrency,
              search: term,
            });

      if (!response?.success) {
        throw new Error(response?.message || 'Failed to fetch products');
      }

      setSearchResults(response?.data || []);
    } catch (error) {
      console.error('Failed to search products:', error);
      setApiError(error?.message || 'Failed to fetch products');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    if (show && parentCurrency) {
      performSearch('');
    }
  }, [show, parentCurrency]);

  const handleAddProduct = product => {
    const listPrice =
      product.listPrice !== null && product.listPrice !== undefined
        ? Number(product.listPrice)
        : 0;

    setSelectedProducts(prev => [
      ...prev,
      {
        ...spreadProductSearchRowOntoLineItem(product),
        uid: generateKey(),
        productId: product.productId,
        productPriceId: product.productPriceId,
        productName: product.productName,
        productCode: product.productCode,
        productCategory: product.productCategory,
        currency: product.currency || parentCurrency || 'USD',
        ...lineItemProductQuantityBounds(product),
        quantity: clampLineQuantity(1, product),
        listPrice,
        salesPrice: listPrice,
        discountPercent: '',
      },
    ]);
  };

  const handleRemoveProduct = uid => {
    setSelectedProducts(prev => {
      const itemToRemove = prev.find(item => item.uid === uid);
      if (itemToRemove?.id) {
        setDeletedIds(prevDeleted => [...prevDeleted, itemToRemove.id]);
      }
      return prev.filter(item => item.uid !== uid);
    });
  };

  const handleQuantityChange = (uid, raw) => {
    setSelectedProducts(prev =>
      prev.map(item => {
        if (item.uid !== uid) {
          return item;
        }
        const trimmed = String(raw ?? '').trim();
        if (trimmed === '') {
          return {
            ...item,
            quantity: resolveProductQuantityBounds(item).min,
          };
        }
        return {
          ...item,
          quantity: clampLineQuantity(Number(trimmed), item),
        };
      })
    );
  };

  const handleQuantityBlur = uid => {
    setSelectedProducts(prev =>
      prev.map(item => {
        if (item.uid !== uid) {
          return item;
        }
        return {
          ...item,
          quantity: clampLineQuantity(item.quantity, item),
        };
      })
    );
  };

  const handleSalesPriceChange = (uid, price) => {
    setSelectedProducts(prev =>
      prev.map(item => {
        if (item.uid !== uid) {
          return item;
        }
        const normalizedPrice = price < 0 ? 0 : price;
        return {
          ...item,
          salesPrice: normalizedPrice,
        };
      })
    );
  };

  const handleDiscountChange = (uid, discount) => {
    setSelectedProducts(prev =>
      prev.map(item => {
        if (item.uid !== uid) {
          return item;
        }

        if (discount === '' || discount === null) {
          return {
            ...item,
            discountPercent: '',
          };
        }

        let normalized = Number(discount);
        if (Number.isNaN(normalized)) {
          normalized = '';
        } else {
          if (normalized < 0) normalized = 0;
          if (normalized > 100) normalized = 100;
        }

        return {
          ...item,
          discountPercent: normalized,
        };
      })
    );
  };

  const handleSearchSubmit = event => {
    event.preventDefault();
    performSearch();
  };

  const handleModalClose = () => {
    setSaveError(null);
    setIsSaving(false);
    onHide();
  };

  const handleNext = () => {
    if (selectedProductsList.length > 0) {
      setCurrentStep(WIZARD_STEPS.EDIT);
    }
  };

  const handleBack = () => {
    setCurrentStep(WIZARD_STEPS.SELECT);
  };

  const handleSave = async () => {
    if (!parentId) {
      setSaveError(ctx.saveParentRequired);
      return;
    }

    const payload = selectedProductsList.map(item => {
      const quantity = clampLineQuantity(item.quantity, item);
      const listPrice =
        item.listPrice !== null && item.listPrice !== undefined
          ? Number(item.listPrice)
          : 0;
      const salesPrice =
        item.salesPrice !== null && item.salesPrice !== undefined
          ? Number(item.salesPrice)
          : listPrice;
      const effectiveDiscount =
        item.discountPercent === '' || item.discountPercent === null || item.discountPercent === undefined
          ? 0
          : Number(item.discountPercent);
      const discountMultiplier =
        Number.isNaN(effectiveDiscount) || effectiveDiscount <= 0
          ? 0
          : Math.min(100, effectiveDiscount) / 100;
      const netSalesPrice = salesPrice * (1 - discountMultiplier);
      const totalPrice = quantity * netSalesPrice;

      return {
        id: item.id,
        productId: item.productId,
        productPriceId: item.productPriceId,
        quantity,
        listPrice,
        salesPrice,
        discountPercent:
          item.discountPercent === '' || item.discountPercent === null || item.discountPercent === undefined
            ? 0
            : Number(item.discountPercent),
        totalPrice,
        description: null,
        name: item.productName || ctx.saveLineFallbackName,
      };
    });

    setIsSaving(true);
    setSaveError(null);

    try {
      let response;
      if (lineItemContext === 'opportunity') {
        response = await OpportunityProductApi.createOpportunityProducts({
          opportunityId: parentId,
          items: payload,
          deletedIds,
        });
      } else if (lineItemContext === 'quote') {
        response = await QuoteProductApi.createQuoteProducts({
          quoteId: parentId,
          items: payload,
          deletedIds,
        });
      } else {
        response = await OrderProductApi.createOrderProducts({
          orderId: parentId,
          items: payload,
          deletedIds,
        });
      }

      if (!response?.success) {
        throw new Error(response?.message || 'Failed to save products');
      }

      if (onSaveSuccess) {
        onSaveSuccess(response.data);
      }

      handleModalClose();
    } catch (error) {
      console.error('Failed to save line items:', error);
      setSaveError(error?.message || 'Failed to save products');
    } finally {
      setIsSaving(false);
    }
  };

  const thClassSearch = (key) => {
    if (key === 'listPrice') {
      return 'text-end';
    }
    if (key === 'action') {
      return 'text-center';
    }
    if (/(price|amount|quantity|qty|cost|percent|rate)$/i.test(String(key))) {
      return 'text-end';
    }
    return '';
  };

  const renderSearchResults = () => {
    if (apiError) {
      return (
        <div className='text-center py-4 text-danger'>
          {apiError}
        </div>
      );
    }

    if (isSearching) {
      return (
        <div className='text-center py-4'>
          <Spinner animation='border' role='status' size='sm' className='me-2' />
          {str('searchingLabel', 'Searching products...')}
        </div>
      );
    }

    if (searchResults.length === 0) {
      return (
        <div className='text-center py-4 text-muted'>
          {str('noSearchResults', 'No products match the current search.')}
        </div>
      );
    }

    return (
      <div className='table-responsive product-results-table'>
        <table className='table table-hover align-middle mb-0'>
          <thead>
            <tr>
              {visSearchKeys.map((key) => (
                <th key={key} className={thClassSearch(key)}>
                  {w.searchColumns[key].label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {searchResults.map(product => {
              const pkey = product.productPriceId || product.productId;
              const alreadySelectedCount = selectedCountByPriceId[pkey] || 0;
              return (
                <tr key={`${product.productPriceId || product.productId}`}>
                  {visSearchKeys.map((key) => {
                    if (key === 'productName') {
                      return (
                        <td key={key}>
                          {product.productName}
                          {alreadySelectedCount > 0 && (
                            <span className='badge bg-light text-muted ms-2'>
                              Added {alreadySelectedCount}
                            </span>
                          )}
                        </td>
                      );
                    }
                    if (key === 'productCode') {
                      return <td key={key}>{product.productCode || '—'}</td>;
                    }
                    if (key === 'productCategory') {
                      return <td key={key}>{product.productCategory || '—'}</td>;
                    }
                    if (key === 'listPrice') {
                      return (
                        <td key={key} className='text-end'>
                          {Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: product.currency || 'USD',
                          }).format(Number(product.listPrice) || 0)}
                        </td>
                      );
                    }
                    if (key === 'action') {
                      return (
                        <td key={key} className='text-center'>
                          <Button
                            size='sm'
                            variant='outline-primary'
                            onClick={() => handleAddProduct(product)}
                          >
                            {alreadySelectedCount > 0
                              ? str('addAgain', 'Add Again')
                              : str('add', 'Add')}
                          </Button>
                        </td>
                      );
                    }
                    return (
                      <td key={key} className='small text-muted'>
                        {getLineItemWizardSearchDisplayedText(product, key)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const thStyleEdit = (key) => {
    if (key === 'quantity') {
      return { width: '120px' };
    }
    if (key === 'discountPercent') {
      return { width: '140px' };
    }
    return undefined;
  };

  const thClassEdit = (key) => {
    if (['listPrice', 'salesPrice', 'lineTotal', 'quantity'].includes(key)) {
      return 'text-end';
    }
    if (key === 'remove') {
      return 'text-center';
    }
    if (/(price|amount|quantity|qty|cost|percent|rate|total)$/i.test(String(key))) {
      return 'text-end';
    }
    return '';
  };

  const renderSelectedProducts = (showPersisted = true) => {
    const displayList = showPersisted
      ? selectedProductsList
      : selectedProductsList.filter(item => !item.id);
    const grandTotal = displayList.reduce((sum, item) => {
      const quantity = Number(item.quantity) || 1;
      const salesPrice = Number(item.salesPrice) || 0;
      const discount =
        item.discountPercent === '' || item.discountPercent === null || item.discountPercent === undefined
          ? 0
          : Math.min(100, Math.max(0, Number(item.discountPercent)));
      const discountMultiplier = discount / 100;
      const netPrice = salesPrice * (1 - discountMultiplier);
      return sum + quantity * netPrice;
    }, 0);

    if (displayList.length === 0) {
      return (
        <div className='text-center py-4 text-muted'>
          {str('emptySelected', 'No products selected yet. Add products from the list above.')}
        </div>
      );
    }

    return (
      <div className='table-responsive selected-products-table'>
        <table className='table table-bordered align-middle mb-0'>
          <thead>
            <tr>
              {visEditKeys.map((key) => (
                <th key={key} className={thClassEdit(key)} style={thStyleEdit(key)}>
                  {w.editColumns[key].label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayList.map(item => {
              const qBounds = resolveProductQuantityBounds(item);
              return (
              <tr key={item.uid}>
                  {visEditKeys.map((key) => {
                    if (key === 'productName') {
                      return <td key={key}>{item.productName}</td>;
                    }
                    if (key === 'productCode') {
                      return <td key={key}>{item.productCode || '—'}</td>;
                    }
                    if (key === 'productCategory') {
                      return <td key={key}>{item.productCategory || '—'}</td>;
                    }
                    if (key === 'quantity') {
                      const boundsHint = formatQuantityBoundsHint(item);
                      return (
                        <td key={key}>
                          <div className='d-flex align-items-center gap-2 flex-nowrap'>
                            <Form.Control
                              type='number'
                              step={1}
                              min={qBounds.min}
                              max={qBounds.max != null ? qBounds.max : undefined}
                              inputMode='numeric'
                              className='flex-grow-1'
                              style={{ minWidth: '3.75rem', maxWidth: '8rem' }}
                              aria-describedby={`qty-hint-${item.uid}`}
                              value={item.quantity}
                              onChange={e => handleQuantityChange(item.uid, e.target.value)}
                              onBlur={() => handleQuantityBlur(item.uid)}
                            />
                            <OverlayTrigger
                              placement='top'
                              overlay={
                                <Tooltip id={`qty-bounds-tip-${item.uid}`}>{boundsHint}</Tooltip>
                              }
                            >
                              <span
                                className='text-muted align-middle flex-shrink-0 d-inline-flex'
                                style={{ cursor: 'help' }}
                                aria-hidden
                              >
                                <FaInfoCircle size={14} />
                              </span>
                            </OverlayTrigger>
                          </div>
                          <span id={`qty-hint-${item.uid}`} className='visually-hidden'>
                            {boundsHint}
                          </span>
                        </td>
                      );
                    }
                    if (key === 'listPrice') {
                      return (
                        <td key={key} className='text-end'>
                          {Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: item.currency || 'USD',
                          }).format(Number(item.listPrice) || 0)}
                        </td>
                      );
                    }
                    if (key === 'salesPrice') {
                      return (
                        <td key={key}>
                          <Form.Control
                            type='number'
                            min={0}
                            value={item.salesPrice}
                            onChange={e => handleSalesPriceChange(item.uid, Number(e.target.value))}
                            className='text-end'
                          />
                        </td>
                      );
                    }
                    if (key === 'discountPercent') {
                      return (
                        <td key={key}>
                          <Form.Control
                            type='number'
                            min={0}
                            max={100}
                            value={item.discountPercent}
                            placeholder='—'
                            onChange={e => handleDiscountChange(item.uid, e.target.value === '' ? '' : Number(e.target.value))}
                            className='text-end'
                          />
                        </td>
                      );
                    }
                    if (key === 'lineTotal') {
                      return (
                        <td key={key} className='text-end'>
                          {(() => {
                            const quantity = Number(item.quantity) || 1;
                            const salesPrice = Number(item.salesPrice) || 0;
                            const discount =
                              item.discountPercent === '' || item.discountPercent === null || item.discountPercent === undefined
                                ? 0
                                : Math.min(100, Math.max(0, Number(item.discountPercent)));
                            const netPrice = salesPrice * (1 - discount / 100);
                            return Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: item.currency || 'USD',
                            }).format(quantity * netPrice);
                          })()}
                        </td>
                      );
                    }
                    if (key === 'remove') {
                      return (
                        <td key={key} className='text-center'>
                          <Button
                            size='sm'
                            variant='outline-danger'
                            onClick={() => handleRemoveProduct(item.uid)}
                          >
                            {str('remove', 'Remove')}
                          </Button>
                        </td>
                      );
                    }
                    return (
                      <td key={key} className='small text-muted'>
                        {getLineItemWizardEditDisplayedText(item, key)}
                      </td>
                    );
                  })}
              </tr>
              );
            })}
          </tbody>
        </table>
        <div className='text-end mt-3 fw-semibold'>
          {str('grandTotal', 'Grand Total:')}{' '}
          {Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: displayList[0]?.currency || 'USD',
          }).format(grandTotal)}
        </div>
      </div>
    );
  };

  const renderStepIndicator = () => (
    <div className='mb-3 small text-muted'>
      <span style={{ fontWeight: currentStep === WIZARD_STEPS.SELECT ? 600 : 400 }}>
        {str('stepSelect', '1. Select Products')}
      </span>
      <span> &nbsp;→&nbsp; </span>
      <span style={{ fontWeight: currentStep === WIZARD_STEPS.EDIT ? 600 : 400 }}>
        {str('stepEdit', '2. Edit Products')}
      </span>
    </div>
  );

  return (
    <Modal
      show={show}
      onHide={handleModalClose}
      size='xl'
      backdrop='static'
      centered
      dialogClassName='opportunity-product-wizard-modal'
    >
      <Modal.Header closeButton>
        <div>
          <Modal.Title>{str('modalTitle', ctx.defaultModalTitle)}</Modal.Title>
          <div className='text-muted small'>
            {ctx.parentIdLabel}: {parentId || '—'} • Currency: {parentCurrency || '—'}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        {initialItemsLoading ? (
          <div className='text-center py-5'>
            <Spinner animation='border' role='status' className='me-2' />
            {str('loadingLineItems', ctx.loadingFallback)}
          </div>
        ) : (
          <>
            {renderStepIndicator()}
            {currentStep === WIZARD_STEPS.SELECT ? (
              <>
                <section className='product-search-section mb-4'>
                  <form onSubmit={handleSearchSubmit} className='d-flex align-items-center gap-3'>
                    <Form.Control
                      type='text'
                      placeholder={str('searchPlaceholder', 'Search by name, code, or category')}
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      disabled={!parentCurrency}
                    />
                    <Button type='submit' variant='primary' disabled={isSearching || !parentCurrency}>
                      {isSearching
                        ? str('searching', 'Searching...')
                        : str('search', 'Search')}
                    </Button>
                  </form>
                  <div className='mt-2 text-muted small'>
                    {parentCurrency
                      ? `${newProductsCount} new product${newProductsCount === 1 ? '' : 's'} selected.`
                      : ctx.cannotSearchWithoutCurrency}
                  </div>
                </section>

                <section className='product-results-section mb-4'>
                  <h6
                    style={{
                      fontFamily: theme.typography.fontFamily.primary,
                      fontWeight: theme.typography.fontWeight.semibold,
                    }}
                  >
                    {str('searchResultsHeading', 'Search Results')}
                  </h6>
                  {renderSearchResults()}
                </section>

                <section className='selected-products-section'>
                  <h6
                    style={{
                      fontFamily: theme.typography.fontFamily.primary,
                      fontWeight: theme.typography.fontWeight.semibold,
                    }}
                  >
                    {str('selectedHeading', 'Selected Products')} ({newProductsCount})
                  </h6>
                  {renderSelectedProducts(false)}
                </section>
              </>
            ) : (
              <section className='edit-products-section'>
                <h6
                  style={{
                    fontFamily: theme.typography.fontFamily.primary,
                    fontWeight: theme.typography.fontWeight.semibold,
                  }}
                >
                  {str('editHeading', 'Edit Selected Products')}
                </h6>
                {renderSelectedProducts(true)}
              </section>
            )}
            {saveError && (
              <div className='alert alert-danger mt-3 mb-0'>
                {saveError}
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className='d-flex justify-content-between'>
        <div className='text-muted small'>
          {currentStep === WIZARD_STEPS.SELECT
            ? str('footerSelect', 'Select products and click Next to review and edit them.')
            : str('footerEdit', 'Adjust quantities, prices, or discounts before saving.')}
        </div>
        <div>
          <Button variant='outline-secondary' className='me-2' onClick={handleModalClose}>
            {str('cancel', 'Cancel')}
          </Button>
          {currentStep === WIZARD_STEPS.SELECT ? (
            <Button
              variant='primary'
              disabled={initialItemsLoading || selectedProductsList.length === 0}
              onClick={handleNext}
            >
              {str('next', 'Next')}
            </Button>
          ) : (
            <>
              <Button
                variant='outline-primary'
                className='me-2'
                onClick={handleBack}
                disabled={initialItemsLoading}
              >
                {str('backButton', 'Back to Add Products')}
              </Button>
              <Button
                variant='primary'
                disabled={
                  initialItemsLoading ||
                  (selectedProductsList.length === 0 && deletedIds.length === 0) ||
                  isSaving
                }
                onClick={handleSave}
              >
                {isSaving ? str('saving', 'Saving...') : str('save', 'Save')}
              </Button>
            </>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default LineItemProductWizard;
